import React from 'react';
import type { Quest } from '../../../../../shared/NPCData';
import { NPC_CONFIG } from '../../../../../shared/content/generated/entities.gen';
import { ItemIcon } from '../../../components/ItemIcon';
import { NpcPortrait } from '../../../components/NpcPortrait';
import { GuidanceMapCard } from './GuidanceMapCard';
import { GUIDANCE_CARD_ASPECT, GUIDANCE_WIDE_CARD_ASPECT } from './guidanceFraming';
import { MobPreview } from './MobPreview';
import type { PlayerWhereabouts } from './playerWhereabouts';
import { prefersReducedMotion } from '../../../../systems/accessibility/motionPrefs';
import './questGuidance.css';

type GuidanceObjective = NonNullable<Quest['guidance']>['objectives'][number];

const OBJECTIVE_KICKER: Record<GuidanceObjective['kind'], string> = {
  kill: 'Hunt here', collect: 'Gather here', explore: 'Explore', talk: 'Find',
};

function objectiveArt(o: GuidanceObjective, name: string): React.ReactNode {
  if (o.kind === 'kill') return <MobPreview mobId={o.targetId} size={36} label={name} />;
  if (o.kind === 'collect') return <span role="img" aria-label={name} data-item-art-id={o.targetId}><ItemIcon itemId={o.targetId} size={32} /></span>;
  if (o.kind === 'talk') return <NpcPortrait npcId={o.targetId} name={name} size={36} />;
  return null;
}

interface QuestGuidanceProps {
  quest: Quest;
  you?: PlayerWhereabouts | null;
}

/**
 * "Where to go" for one quest (plan 02 §2.3): the objective area while the
 * work is still to do, then the turn-in NPC. Objectives with no knowable place
 * still show their mob or item, so the player knows what to look for.
 * Renders nothing for quests without compiled guidance.
 */
export const QuestGuidance: React.FC<QuestGuidanceProps> = ({ quest, you }) => {
  const guidance = quest.guidance;
  if (!guidance) return null;
  const nameOf = (o: GuidanceObjective) => quest.objectives.find(q => q.targetId === o.targetId)?.targetName ?? o.targetId;
  const countOf = (o: GuidanceObjective) => {
    const row = quest.objectives.find(q => q.targetId === o.targetId);
    return row && (o.kind === 'kill' || o.kind === 'collect') ? ` ×${row.required}` : '';
  };
  const youOn = (mapId: string) => (you && you.mapId === mapId ? { x: you.x, y: you.y } : null);
  const workLeft = !quest.isTurnInReady && !quest.isCompleted;
  const objectives = workLeft ? guidance.objectives : [];
  const located = objectives.filter(o => o.location);
  // One map per area: objectives sharing a map share a card (Brambleway's boars and bees).
  const firstOnMap = located.filter((o, i) => located.findIndex(p => p.location!.mapId === o.location!.mapId) === i);
  const unlocated = objectives.filter(o => !o.location && o.kind !== 'explore');
  const turnInName = NPC_CONFIG[guidance.turnIn.npcId]?.name ?? quest.endNpcName ?? guidance.turnIn.npcId;
  // Cards pair up side by side; an odd card out takes the row at the same height.
  const cardCount = firstOnMap.length + 1;
  const aspectFor = (index: number) => (cardCount % 2 === 1 && index === cardCount - 1
    ? GUIDANCE_WIDE_CARD_ASPECT : GUIDANCE_CARD_ASPECT);

  return <section className="npc-guidance" aria-label="Where to go" data-testid={`quest-guidance-${quest.id}`}
    data-reduce-motion={prefersReducedMotion() || undefined}>
    {unlocated.length > 0 && <ul className="npc-guidance-targets" aria-label="What to look for">
      {unlocated.map(o => <li key={o.targetId}>{objectiveArt(o, nameOf(o))}<span>{nameOf(o)}{countOf(o)}</span></li>)}
    </ul>}
    <div className="npc-guidance-cards" data-card-count={cardCount}>
      {firstOnMap.map((o, index) => {
        const sameMap = located.filter(p => p.location!.mapId === o.location!.mapId);
        const location = { mapId: o.location!.mapId, points: sameMap.flatMap(p => p.location!.points) };
        return <GuidanceMapCard key={`obj-${o.location!.mapId}`} variant="objective" location={location}
          kicker={OBJECTIVE_KICKER[o.kind]} title={sameMap.map(p => `${nameOf(p)}${countOf(p)}`).join(' · ')}
          art={<span className="npc-guidance-art-row">{sameMap.map(p => <React.Fragment key={p.targetId}>{objectiveArt(p, nameOf(p))}</React.Fragment>)}</span>}
          you={youOn(location.mapId)} testId={`quest-guidance-objective-${quest.id}-${location.mapId}`}
          aspect={aspectFor(index)} />;
      })}
      <GuidanceMapCard variant="turn-in" location={guidance.turnIn.location}
        kicker={quest.isTurnInReady ? 'Turn in to' : 'Report to'} title={turnInName}
        art={<NpcPortrait npcId={guidance.turnIn.npcId} name={turnInName} size={36} />}
        you={youOn(guidance.turnIn.location.mapId)} testId={`quest-guidance-turn-in-${quest.id}`}
        aspect={aspectFor(cardCount - 1)} />
    </div>
  </section>;
};
